import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { Calendar, Columns2, Printer, ArrowLeft } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PayslipPDF from './PayslipPdf';

const PayslipDetail = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  const [token, setToken] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOsci, setSelectedOsci] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Acquire token
  useEffect(() => {
    const acquireToken = async () => {
      if (!accounts || !accounts.length) {
        try {
          const response = await instance.handleRedirectPromise();
          if (!response && !accounts.length) {
            instance.loginRedirect(loginRequest);
          }
        } catch (err) {
          console.error("Error during loginRedirect or handleRedirectPromise:", err);
        }
      } else {
        const request = { ...loginRequest, account: accounts[0] };
        try {
          const response = await instance.acquireTokenSilent(request);
          setToken(response.accessToken);
        } catch (silentError) {
          console.warn("Silent token acquisition failed, trying popup...", silentError);
          try {
            const popupResponse = await instance.acquireTokenPopup(request);
            setToken(popupResponse.accessToken);
          } catch (popupError) {
            console.error("Popup token acquisition failed:", popupError);
          }
        }
      }
    };
    acquireToken();
  }, [accounts, instance]);

  // Fetch payslips
  useEffect(() => {
    const fetchPayslips = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          "https://hrisapi-ehfsadbdckg9gmeq.westeurope-01.azurewebsites.net/api/payslip/getAll",
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error(`Chyba při stahování dat: ${response.statusText}`);
        const data = await response.json();
        setPayslips(data);
      } catch (fetchError) {
        console.error("API call failed:", fetchError);
        setError(fetchError.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPayslips();
  }, [token]);

  // OSČI selection
  const availableOsci = useMemo(
    () => (payslips.length ? [...new Set(payslips.map(p => p.osci))] : []),
    [payslips]
  );
  useEffect(() => {
    if (availableOsci.length && !selectedOsci) setSelectedOsci(availableOsci[0]);
  }, [availableOsci, selectedOsci]);

  // Year selection
  const availableYears = useMemo(() => {
    if (!selectedOsci) return [];
    return [...new Set(payslips.filter(p => p.osci === selectedOsci).map(p => p.rok))].sort((a, b) => b - a);
  }, [payslips, selectedOsci]);
  useEffect(() => {
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    } else if (!availableYears.length) {
      setSelectedYear(null);
    }
  }, [availableYears, selectedYear]);

  // Month selection
  const availableMonths = useMemo(() => {
    if (!selectedOsci || !selectedYear) return [];
    return [...new Set(
      payslips
        .filter(p => p.osci === selectedOsci && p.rok === selectedYear)
        .map(p => p.mesic)
    )].sort((a, b) => b - a);
  }, [payslips, selectedOsci, selectedYear]);
  useEffect(() => {
    if (availableMonths.length && !availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[0]);
    } else if (!availableMonths.length) {
      setSelectedMonth(null);
    }
  }, [availableMonths, selectedMonth]);

  const payslip = useMemo(() => {
    return payslips.find(
      p => p.osci === selectedOsci && p.rok === selectedYear && p.mesic === selectedMonth
    ) || null;
  }, [payslips, selectedOsci, selectedYear, selectedMonth]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
          <div className="text-lg font-medium text-gray-600">Načítám výplatní lístky...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-600">Nastala chyba při načítání dat: {error}</div>;
  }

  const pdfFileName = payslip
    ? `VypListek_${payslip.osci}_${String(payslip.mesic).padStart(2, '0')}_${payslip.rok}.pdf`
    : 'vypis.pdf';
  const pdfData = payslip ? [payslip] : [];

  return (
    <div className="w-full mx-auto bg-white sm:rounded-2xl shadow-sm overflow-hidden sm:max-w-2xl my-0">
      {/* Header with back button */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white p-6">
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap gap-4">
        <div className="hidden sm:flex items-center flex-shrink-0 space-x-2">
            <button
              onClick={onBack}
              className="px-3 py-2 rounded-md hover:bg-white/20 transition-colors duration-200 focus:outline-none flex items-center gap-2"
              aria-label="Zpět na rozcestník"
            >
              <ArrowLeft size={24} />
              <div className="text-lg font-semibold whitespace-nowrap">Výplatní lístek</div>
            </button>
          </div>

          {/* Filters */}
          <div className={`flex items-center space-x-4 min-w-0 ${availableOsci.length === 1 ? 'justify-end flex-grow' : 'justify-start'}`}>
            {availableOsci.length > 1 && (
              <div className="flex items-center space-x-2 pr-4 sm:pr-10">
                <Columns2 size={20} />
                <select
                  id="osciSelect"
                  value={selectedOsci}
                  onChange={e => setSelectedOsci(e.target.value)}
                  className="bg-transparent border-b border-white/50 focus:outline-none text-xl py-2"
                >
                  {availableOsci.map(osci => (
                    <option key={osci} value={osci} className="bg-indigo-600">{osci}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Calendar size={20} />
              <select
                value={selectedYear || ''}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="bg-transparent border-b border-white/50 focus:outline-none text-xl p-2"
                disabled={!selectedOsci || !availableYears.length}
              >
                {!availableYears.length && <option disabled value="">N/A</option>}
                {availableYears.map(year => (
                  <option key={year} value={year} className="bg-indigo-600">{year}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <select
                value={selectedMonth || ''}
                onChange={e => setSelectedMonth(Number(e.target.value))}
                className="bg-transparent border-b border-white/50 focus:outline-none text-xl py-2"
                disabled={!selectedYear || !availableMonths.length}
              >
                {!availableMonths.length && <option disabled value="">N/A</option>}
                {availableMonths.map(month => (
                  <option key={month} value={month} className="bg-indigo-600">
                    {month < 10 ? `0${month}` : month}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Payslip content */}
      <div className="p-2 sm:p-6 lg:p-6 bg-gray-50">
        {!payslip && (
          <div className="text-center text-gray-500 py-10">
            Vyberte prosím platné období pro zobrazení výplatního lístku.
          </div>
        )}

        {payslip && (
          <>
            {payslip.listek.map((section, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg sm:m-4 shadow-md mb-4 overflow-hidden"
              >
                <div className="bg-blue-100 p-3 border-b border-blue-200">
                  <h3 className="font-bold text-blue-800">{section.sekce}</h3>
                </div>
                <div className="p-4">
                  {section.polozka.map((item, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-start py-2 border-b last:border-b-0 border-gray-200"
                    >
                      <div>
                        <p className="font-medium text-gray-800">{item.popis}</p>
                        {item.hodiny != null && <p className="text-sm text-gray-600">Hodiny: {item.hodiny}</p>}
                        {item.od != null && item.do != null && <p className="text-sm text-gray-600">Období: {item.od} - {item.do}</p>}
                        {item.txt && <p className="text-sm text-gray-600">{item.txt}</p>}
                        {item.zaklad != null && <p className="text-sm text-gray-600">Základ: {item.zaklad.toLocaleString('cs-CZ')}</p>}
                      </div>
                      <div className="text-right">
                        {item.castka != null && (
                          <p className={`text-blue-700 ${item.typPolozky === 'bold' ? 'font-bold' : ''}`}>
                            {item.typPolozky === 'DD'
                              ? item.castka.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : item.castka.toLocaleString('cs-CZ')}
                            {item.mj && ` ${item.mj}`}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end mb-4 mr-0 sm:mr-4">
              <PDFDownloadLink
                document={<PayslipPDF data={pdfData} userName={accounts[0]?.name || accounts[0]?.username} />}
                fileName={pdfFileName}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                {({ loading: pdfLoading }) =>
                  pdfLoading ? 'Generuji PDF...' : (<><Printer size={18} className="mr-2" />Export do PDF</>)
                }
              </PDFDownloadLink>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

PayslipDetail.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default PayslipDetail;
